import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, ReplaySubject, Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../api/api.service';

/** Where a sent message's recipients ended up, from delivery receipts. */
export interface DeliverySummary {
  total: number;
  delivered: number;
  failed: number;
  /**
   * No final receipt yet: either still queued to send, or sent and waiting
   * for the network to report back (accepted, en route, unknown).
   */
  inFlight: number;
  /** Of inFlight, how many have not left the gateway yet (when known). */
  queued?: number;
}

// Receipt states that mean the SMS will not arrive. Everything that is
// neither delivered nor one of these is still in flight (queued, accepted,
// en route, pending, scheduled, unknown).
const FAILED_STATES = ['REJECTED', 'UNDELIVERABLE', 'EXPIRED', 'DELETED', 'SKIPPED'];
/** Still waiting at the gateway: not handed to a network yet. */
const QUEUED_STATES = ['QUEUED', 'PENDING', 'SCHEDULED'];

/** Receipts keep arriving after a send, so cached counts expire. */
const CACHE_MS = 60_000;
/** Each summary is three count requests; cap how many summaries load at once. */
const CONCURRENCY = 4;

/** Live counts: poll this often while receipts are arriving… */
const LIVE_MIN_MS = 10_000;
/** …and back off to this while the counts stand still. */
const LIVE_MAX_MS = 120_000;
/** Views polling the same message within this window share one request. */
const LIVE_SHARE_MS = 5_000;

/** SMS validity: past this, a receipt that has not come back will not. */
export const RECEIPT_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Can more receipts still arrive? While a message is sending, or within the
 * receipt window after it went out. Without a send time, assume they can.
 */
export function receiptsStillDue(status: string | null | undefined, sentAt: Date | string | null | undefined): boolean {
  if (/^active$/i.test(status ?? '')) return true;
  const sent = sentAt ? new Date(sentAt).getTime() : NaN;
  if (Number.isNaN(sent)) return true;
  return Date.now() - sent < RECEIPT_WINDOW_MS;
}

/**
 * Delivery outcome per message, built from three cheap count queries on
 * engagement-message-reports (all, delivered, failed) instead of loading
 * every recipient row the way the report page does.
 */
@Injectable({ providedIn: 'root' })
export class DeliveryService {
  private api = inject(ApiService);
  private zone = inject(NgZone);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private cache = new Map<string, { at: number; result: ReplaySubject<DeliverySummary> }>();
  private queue = new Subject<{ uuid: string; result: ReplaySubject<DeliverySummary> }>();

  constructor() {
    this.queue
      .pipe(
        mergeMap(
          ({ uuid, result }) =>
            this.load(uuid).pipe(
              tap((summary) => {
                result.next(summary);
                result.complete();
              }),
              catchError((error) => {
                // Let the next view retry instead of caching the failure.
                this.cache.delete(uuid);
                result.error(error);
                return EMPTY;
              })
            ),
          CONCURRENCY
        )
      )
      .subscribe();
  }

  /** Counts for one send, from cache when younger than maxAgeMs. */
  summary(messageUuid: string, maxAgeMs = CACHE_MS): Observable<DeliverySummary> {
    if (!this.isBrowser || !messageUuid) return EMPTY;

    const cached = this.cache.get(messageUuid);
    if (cached && Date.now() - cached.at < maxAgeMs) {
      return cached.result.asObservable();
    }

    const result = new ReplaySubject<DeliverySummary>(1);
    this.cache.set(messageUuid, { at: Date.now(), result });
    this.queue.next({ uuid: messageUuid, result });
    return result.asObservable();
  }

  /**
   * Counts that keep themselves current. The first value comes from cache
   * when there is one; after that the counts are fetched again every 10 s
   * while `keepPolling` says receipts are still coming, backing off to 2 min
   * while nothing changes. It pauses while the tab is hidden and completes
   * once the send has settled.
   */
  live(
    messageUuid: string,
    keepPolling: (summary: DeliverySummary) => boolean = (summary) => summary.inFlight > 0
  ): Observable<DeliverySummary> {
    if (!this.isBrowser || !messageUuid) return EMPTY;

    return new Observable<DeliverySummary>((subscriber) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let request: Subscription | undefined;
      let wait = LIVE_MIN_MS;
      let last = '';

      const onVisible = () => {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', onVisible);
        poll(LIVE_SHARE_MS);
      };

      // The timer sits outside Angular's zone, so a waiting poll never keeps
      // the app from settling; the request itself runs inside it.
      const schedule = () => {
        timer = this.zone.runOutsideAngular(() =>
          setTimeout(() => {
            if (document.hidden) document.addEventListener('visibilitychange', onVisible);
            else poll(LIVE_SHARE_MS);
          }, wait)
        );
      };

      const poll = (maxAgeMs: number) =>
        this.zone.run(() => {
          request?.unsubscribe();
          request = this.summary(messageUuid, maxAgeMs).subscribe({
            next: (summary) => {
              const key = `${summary.total}/${summary.delivered}/${summary.failed}`;
              wait = key === last ? Math.min(wait * 2, LIVE_MAX_MS) : LIVE_MIN_MS;
              last = key;
              subscriber.next(summary);
              if (keepPolling(summary)) schedule();
              else subscriber.complete();
            },
            error: (error) => {
              // Before any counts, the view shows the error; after, keep the
              // last counts on screen and try again later.
              if (!last) {
                subscriber.error(error);
                return;
              }
              wait = Math.min(wait * 2, LIVE_MAX_MS);
              schedule();
            },
          });
        });

      poll(CACHE_MS);

      return () => {
        clearTimeout(timer);
        request?.unsubscribe();
        document.removeEventListener('visibilitychange', onVisible);
      };
    });
  }

  private load(uuid: string): Observable<DeliverySummary> {
    // The message uuid alone identifies the send. Filtering on the report's
    // organisation field too would turn rows that lack it into false zeros.
    const count = (states?: string[]) =>
      this.api
        .get<number>('blackkonnectcxpengagement/api/engagement-message-reports/count', {
          'messageUuid.equals': uuid,
          ...(states ? { 'deliveryStatus.in': states } : {}),
        })
        .pipe(map((value) => Number(value) || 0));

    return forkJoin({
      total: count(),
      delivered: count(['DELIVERED']),
      failed: count(FAILED_STATES),
    }).pipe(
      switchMap(({ total, delivered, failed }) => {
        const inFlight = Math.max(0, total - delivered - failed);
        const summary: DeliverySummary = { total, delivered, failed, inFlight, queued: 0 };
        // Only worth a fourth request when something has no receipt yet.
        if (!inFlight) return of(summary);
        return count(QUEUED_STATES).pipe(map((queued) => ({ ...summary, queued: Math.min(queued, inFlight) })));
      })
    );
  }
}

/** Empty summary, for callers that need a value before the first load. */
export const NO_DELIVERY: DeliverySummary = { total: 0, delivered: 0, failed: 0, inFlight: 0, queued: 0 };

/**
 * "12 with no receipt yet (3 not sent yet)": the recipients without a final
 * receipt, split so "queued" never reads as the whole group.
 */
export function noReceiptPhrase(summary: DeliverySummary, receiptsDue: boolean): string {
  const base = `${summary.inFlight.toLocaleString('en-US')} with no receipt${receiptsDue ? ' yet' : ''}`;
  const queued = summary.queued ?? 0;
  return queued ? `${base} (${queued.toLocaleString('en-US')} not sent yet)` : base;
}

export function deliveredShare(summary: DeliverySummary): number {
  return summary.total ? summary.delivered / summary.total : 0;
}

/** "100%", "99.4%", "61%": a decimal only where it changes the meaning. */
export function formatShare(share: number): string {
  const percent = share * 100;
  if (percent >= 99 && percent < 100) return `${Math.floor(percent * 10) / 10}%`;
  return `${Math.floor(percent)}%`;
}
